import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { planAPI } from '../services/api';
import { CheckIcon } from '@heroicons/react/24/outline';
import { formatBytes } from '../utils/format';
import { Plan, PeriodType, periodLabels } from '../types';
import { showErrorToast, showToast } from '../utils/toast';
import { PlanContent, PlanContentMobile } from '../components/Plancontent';

export const Plans: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriods, setSelectedPeriods] = useState<Record<number, PeriodType>>({});

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await planAPI.getPlans();
      const plansData = response.data.data || [];
      setPlans(plansData);

      // Initialize selected periods to month for each plan
      const initialPeriods: Record<number, PeriodType> = {};
      plansData.forEach((plan: Plan) => {
        if (plan.month_price) initialPeriods[plan.id] = 'month_price';
        else if (plan.quarter_price) initialPeriods[plan.id] = 'quarter_price';
        else if (plan.half_year_price) initialPeriods[plan.id] = 'half_year_price';
        else if (plan.year_price) initialPeriods[plan.id] = 'year_price';
        else if (plan.onetime_price) initialPeriods[plan.id] = 'onetime_price';
        else if (plan.reset_price) initialPeriods[plan.id] = 'reset_price';
      });
      setSelectedPeriods(initialPeriods);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = (planId: number) => {
    const selectedPeriod = selectedPeriods[planId];
    navigate(`/order/${planId}/${selectedPeriod}`);
  };

  const getPlanPrice = (plan: Plan, period: PeriodType): number => {
    switch (period) {
      case 'month_price': return plan.month_price || 0;
      case 'quarter_price': return plan.quarter_price || 0;
      case 'half_year_price': return plan.half_year_price || 0;
      case 'year_price': return plan.year_price || 0;
      case 'two_year_price': return plan.two_year_price || 0;
      case 'three_year_price': return plan.three_year_price || 0;
      case 'onetime_price': return plan.onetime_price || 0;
      case 'reset_price': return plan.reset_price || 0;
      default: return 0;
    }
  };

  const getAvailablePeriods = (plan: Plan): PeriodType[] => {
    const periods: PeriodType[] = [];
    if (plan.month_price) periods.push('month_price');
    if (plan.quarter_price) periods.push('quarter_price');
    if (plan.half_year_price) periods.push('half_year_price');
    if (plan.year_price) periods.push('year_price');
    if (plan.two_year_price) periods.push('two_year_price');
    if (plan.three_year_price) periods.push('three_year_price');
    if (plan.onetime_price) periods.push('onetime_price');
    if (plan.reset_price) periods.push('reset_price');
    return periods;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors">订阅计划</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 transition-colors">
          选择适合您的订阅计划，享受高速稳定的网络服务。
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map((plan) => {
          const availablePeriods = getAvailablePeriods(plan);
          const selectedPeriod = selectedPeriods[plan.id] || availablePeriods[0];
          const price = getPlanPrice(plan, selectedPeriod);
          const isCurrentPlan = user?.plan_id === plan.id;

          return (
            <div
              key={plan.id}
              className={`relative bg-white rounded-lg shadow-lg overflow-hidden ${isCurrentPlan ? 'ring-2 ring-primary-500' : ''
                }`}
            >
              {isCurrentPlan && (
                <div className="absolute top-0 right-0 bg-primary-500 text-white px-3 py-1 text-xs font-medium rounded-bl-lg">
                  当前计划
                </div>
              )}

              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {plan.name}
                </h3>

                <div className="mb-4">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">
                      ¥{(price / 100).toFixed(0)}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">
                      /{periodLabels[selectedPeriod]}
                    </span>
                  </div>
                </div>

                {/* {availablePeriods.length > 1 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      计费周期
                    </label>
                    <select
                      value={selectedPeriod}
                      onChange={(e) => setSelectedPeriods({
                        ...selectedPeriods,
                        [plan.id]: e.target.value as PeriodType
                      })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                    >
                      {availablePeriods.map((period) => (
                        <option key={period} value={period}>
                          {periodLabels[period]} - ¥{(getPlanPrice(plan, period) / 100).toFixed(0)}
                        </option>
                      ))}
                    </select>
                  </div>
                )} */}

                <div className="space-y-3 mb-6">
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm text-gray-600">
                      流量: {plan.transfer_enable}GB
                    </span>
                  </div>
                  {plan.speed_limit && (
                    <div className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600">
                        限速: {plan.speed_limit} Mbps
                      </span>
                    </div>
                  )}
                  {plan.capacity_limit && (
                    <div className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600">
                        设备限制: {plan.capacity_limit} 台
                      </span>
                    </div>
                  )}
                </div>

                {plan.content && (
                  <div className="mb-6">
                    <div className="block sm:hidden">
                      <PlanContentMobile content={plan.content} />
                    </div>
                    <div className="hidden sm:block">
                      <PlanContent content={plan.content} />
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isCurrentPlan && plan.renew === 0}
                  className={`
                    w-full py-3 px-6 rounded-lg text-base font-semibold transition-all duration-200 transform
                    focus:outline-none focus:ring-4 focus:ring-offset-2 shadow-md hover:shadow-lg
                    ${isCurrentPlan && plan.renew === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : isCurrentPlan
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 focus:ring-orange-300 hover:scale-105'
                        : 'bg-gradient-to-r from-primary-600 to-blue-600 text-white hover:from-primary-700 hover:to-blue-700 focus:ring-primary-300 hover:scale-105'
                    }
                  `}
                >
                  <div className="flex items-center justify-center space-x-2">
                    {isCurrentPlan && plan.renew !== 0 ? (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span>立即续费</span>
                      </>
                    ) : isCurrentPlan && plan.renew === 0 ? (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                        </svg>
                        <span>不可续费</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span>立即订阅</span>
                      </>
                    )}
                  </div>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {plans.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">暂无可用的订阅计划</p>
        </div>
      )}
    </div>
  );
};